import Head from 'next/head'
import { useTranslation } from '@i18n'
import { SearchArticles_searchArticles_articles } from '@apollo-types/SearchArticles'
import { searchUrl, publicUrl } from '@libs/appUrl'
import { ArticleSearchType } from '@apollo-types/globalTypes'

type SearchHeadProps = {
  publishedAt?: string
  modifiedAt?: string
  keyword?: string
  currentPage?: number
  articles?: SearchArticles_searchArticles_articles[]
  trendingKeywords?: string[]
  sort: ArticleSearchType
}

function SearchHead(props: SearchHeadProps) {
  const {
    currentPage,
    keyword,
    articles = [],
    trendingKeywords,
    publishedAt,
    modifiedAt,
    sort,
  } = props

  const firstArticle = articles?.[0]
  const { t } = useTranslation('search')
  const makeTitle = () => {
    if (!keyword) return t(['defaultMetaTitle', '搜尋精選文章｜PopDaily 波波黛莉'])
    return currentPage > 1
      ? t(`searchType=${sort}.metaTitleWithPage`, { keyword, page: currentPage })
      : t(`searchType=${sort}.metaTitle`, { keyword })
  }

  const makeDescription = () => {
    if (!keyword) {
      return t([
        'defaultMetaDescription',
        '我們一起分享每一天的大小事，創造生活中的新鮮事，用生動的筆觸，跟上世界流行的步伐，紀錄最值得細細品味的女孩生活。波波黛莉最新文章相關標籤：Popdaily、波波黛莉、波波文章、波波貼文、創作者、探索',
      ])
    }

    const joiner = sort === ArticleSearchType.RELEVANT ? '、' : ', '
    const titles = articles
      .slice(0, 3)
      .map((article) => article?.metaTitle ?? '')
      .join(joiner)

    return t(`searchType=${sort}.metaDescription`, { titles, keyword })
  }

  const getWebPageKeywords = () => {
    if (keyword) {
      return firstArticle?.hashTags
    } else {
      return trendingKeywords.slice(0, 10)
    }
  }

  const getCoverImageFromFirstArticle = () => {
    const coverImage = firstArticle?.coverImage?.croppedUrl
    return coverImage
  }

  const webPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: makeTitle(),
    description: makeDescription(),
    keywords: getWebPageKeywords(),
    dateCreated: publishedAt,
    datePublished: publishedAt,
    dateModified: modifiedAt,
    primaryImageOfPage: {
      '@type': 'ImageObject',
      contentUrl: getCoverImageFromFirstArticle() || publicUrl('/images/popdaily.png'),
    },
  }

  const itemList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: [
      (articles.slice(0, 10) || []).map((article, index) => ({
        '@type': 'ListItem',
        name: article?.title ?? '',
        image: article?.coverImage?.croppedUrl ?? '',
        url: article?.url ?? '',
        position: index + 1,
      })),
    ],
  }

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'PopDaily 波波黛莉的異想世界',
        item: publicUrl(),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '你正在PopDaily搜尋',
        item: publicUrl('/search'),
      },
    ],
  }
  if (keyword) {
    const breadcrumbListPosition3 = {
      '@type': 'ListItem',
      position: 3,
      name: keyword,
      item: publicUrl(`/search/${keyword}${currentPage > 1 ? `/${currentPage}` : ''}`),
    }
    breadcrumbList.itemListElement.push(breadcrumbListPosition3)
  }

  return (
    <Head>
      <title key="title">{makeTitle()}</title>
      <meta key="description" name="description" content={makeDescription()} />
      <link key="canonical" rel="canonical" href={searchUrl(keyword)} />
      <link
        type="application/rss+xml"
        rel="alternate"
        title={makeTitle()}
        href="/api/v2/search/rss"
      />
      {/**
       * no index 以下頁面：
       *   沒有搜尋結果
       *   第二頁之後
       *   搜尋類別是熱門 (HOT)
       */}
      {(articles.length === 0 || currentPage > 1 || sort === ArticleSearchType.HOT) && (
        <meta key="robots" name="robots" content="noindex" />
      )}

      {/* WebPage */}
      <script
        key="WebPage"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPage) }}
      />
      {/* ItemList */}
      {articles.length > 0 && (
        <script
          key="ItemList"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemList) }}
        />
      )}
      {/* BreadcrumbList */}
      <script
        key="BreadcrumbList"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbList) }}
      />
    </Head>
  )
}

export default SearchHead
